import { type CSSProperties } from 'react';
import { NavIcon } from './shared';

// ─── Dummy Data ───

export interface AllergyItem {
  name: string;
  severity?: 'moderate' | 'mild' | 'severe';
  reaction: string;
}

export interface MedicationInfo {
  bloodGroupAbo: string;
  bloodGroupRh: string;
  pastMedication: string;
  currentMedication: string;
  medicalHistory: string;
  notes: string;
  alerts: string;
}

export const DEFAULT_ALLERGIES: AllergyItem[] = [
  { name: 'Penicillin', severity: 'moderate', reaction: 'Rash' },
  { name: 'Dust', severity: 'mild', reaction: 'Sneezing, watery eyes' },
];

// ─── Severity Badge ───

const severityStyles: Record<string, CSSProperties> = {
  severe: {
    background: 'var(--color-danger-bg)',
    color: 'var(--color-danger-text)',
  },
  moderate: {
    background: 'var(--color-warning-bg)',
    color: 'var(--color-warning-text)',
  },
  mild: {
    background: 'var(--color-info-bg)',
    color: '#1e40af',
  },
};

function SeverityBadge({ severity }: { severity: string }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 20,
        padding: '0 var(--sp-2)',
        borderRadius: 'var(--radius-sm)',
        fontSize: 'var(--font-size-sm)',
        fontWeight: 'var(--font-weight-medium)',
        lineHeight: 'var(--line-height-sm)',
        ...severityStyles[severity],
      }}
    >
      {severity}
    </span>
  );
}

// ─── Allergy Card ───

function AllergyCard({ allergy }: { allergy: AllergyItem }) {
  return (
    <div
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        padding: '17px',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-2)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
        <span
          style={{
            fontSize: 'var(--font-size-base)',
            fontWeight: 'var(--font-weight-semi)',
            lineHeight: 'var(--line-height-base)',
            letterSpacing: '-0.15px',
            color: 'var(--text)',
          }}
        >
          {allergy.name}
        </span>
        {allergy.severity && <SeverityBadge severity={allergy.severity} />}
      </div>
      <span style={{ fontSize: 'var(--font-size-sm)', lineHeight: 'var(--line-height-sm)' }}>
        <span style={{ fontWeight: 'var(--font-weight-medium)', color: 'var(--text-muted)' }}>
          Reaction:
        </span>
        <span style={{ fontWeight: 'var(--font-weight-regular)', color: 'var(--text-muted)' }}>
          {' '}
          {allergy.reaction}
        </span>
      </span>
    </div>
  );
}

// ─── Allergies Detail View (sidebar content) ───

// ─── Detail Field Row ───

const detailLabelStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  lineHeight: 'var(--line-height-sm)',
  color: 'var(--text-placeholder)',
};

const detailValueStyle: CSSProperties = {
  fontSize: 'var(--font-size-base)',
  fontWeight: 'var(--font-weight-medium)',
  lineHeight: 'var(--line-height-base)',
  letterSpacing: '-0.15px',
  color: 'var(--text)',
};

function DetailField({ label, value }: { label: string; value: string }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={detailLabelStyle}>{label}</span>
      <span style={detailValueStyle}>{value}</span>
    </div>
  );
}

// ─── Section Card (reusable) ───

function InfoSectionCard({
  icon,
  title,
  children,
}: {
  icon: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        background: 'var(--bg-surface-2)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--sp-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-3)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
        <div style={{ color: 'var(--text-muted)', flexShrink: 0 }}>
          <NavIcon name={icon} />
        </div>
        <span
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-semi)',
            lineHeight: 'var(--line-height-sm)',
            letterSpacing: '0.3px',
            textTransform: 'uppercase',
            color: 'var(--text-muted)',
          }}
        >
          {title}
        </span>
      </div>
      {children}
    </div>
  );
}

export function AllergiesDetailView({
  onUpdateMedication,
  allergies,
  medicationInfo,
}: {
  onUpdateMedication: () => void;
  allergies: AllergyItem[];
  medicationInfo?: MedicationInfo;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span
          style={{
            fontSize: 'var(--font-size-lg)',
            fontWeight: 'var(--font-weight-semi)',
            lineHeight: 'var(--line-height-lg)',
            letterSpacing: '-0.31px',
            color: 'var(--text)',
          }}
        >
          Allergies
        </span>
        <button
          onClick={onUpdateMedication}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--sp-2)',
            height: 32,
            padding: '0 var(--sp-4)',
            borderRadius: 'var(--radius-full)',
            border: '1px solid var(--color-warning-border)',
            background: 'var(--bg-surface)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            lineHeight: 'var(--line-height-sm)',
            color: 'var(--color-warning)',
            cursor: 'pointer',
          }}
        >
          <NavIcon name="alert-triangle" /> Update Medication
        </button>
      </div>

      {/* Critical Information Card */}
      <div
        style={{
          borderRadius: 'var(--radius-lg)',
          border: '1px solid rgba(245, 158, 11, 0.3)',
          padding: '17px',
          background: 'linear-gradient(165deg, var(--color-warning-bg), rgba(255, 251, 235, 0.5))',
        }}
      >
        <div style={{ display: 'flex', gap: 'var(--sp-4)' }}>
          <div style={{ color: 'var(--color-warning)', flexShrink: 0, marginTop: 2 }}>
            <NavIcon name="shield-alert" />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
            <span
              style={{
                fontSize: 'var(--font-size-base)',
                fontWeight: 'var(--font-weight-semi)',
                lineHeight: 'var(--line-height-base)',
                letterSpacing: '-0.15px',
                color: 'var(--color-warning-text)',
              }}
            >
              Critical Information
            </span>
            <span
              style={{
                fontSize: 'var(--font-size-sm)',
                lineHeight: 'var(--line-height-sm)',
                color: '#78350f',
              }}
            >
              Review allergies before prescribing or administering any medication.
            </span>
          </div>
        </div>
      </div>

      {/* Current Allergies Card */}
      <div
        style={{
          background: 'var(--bg-surface-2)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--sp-5)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-4)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span
            style={{
              fontSize: 'var(--font-size-base)',
              fontWeight: 'var(--font-weight-semi)',
              lineHeight: 'var(--line-height-base)',
              letterSpacing: '-0.15px',
              color: 'var(--text)',
            }}
          >
            Current Allergies
          </span>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 20,
              height: 20,
              padding: '0 var(--sp-2)',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--border)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              lineHeight: 'var(--line-height-sm)',
              color: 'var(--text-muted)',
            }}
          >
            {allergies.length}
          </span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
          {allergies.map((a) => (
            <AllergyCard key={a.name} allergy={a} />
          ))}
        </div>
      </div>

      {/* Blood Type */}
      {medicationInfo && (medicationInfo.bloodGroupAbo || medicationInfo.bloodGroupRh) && (
        <InfoSectionCard icon="droplet" title="Blood Type">
          <div style={{ display: 'flex', gap: 'var(--sp-5)' }}>
            <DetailField label="ABO Group" value={medicationInfo.bloodGroupAbo} />
            <DetailField label="Rh Antigen" value={medicationInfo.bloodGroupRh} />
          </div>
        </InfoSectionCard>
      )}

      {/* Medication History */}
      {medicationInfo &&
        (medicationInfo.currentMedication ||
          medicationInfo.pastMedication ||
          medicationInfo.medicalHistory) && (
          <InfoSectionCard icon="pill" title="Medication History">
            <DetailField label="Current Medication" value={medicationInfo.currentMedication} />
            <DetailField label="Past Medication" value={medicationInfo.pastMedication} />
            <DetailField label="Medical History" value={medicationInfo.medicalHistory} />
          </InfoSectionCard>
        )}

      {/* Notes & Alerts */}
      {medicationInfo && (medicationInfo.notes || medicationInfo.alerts) && (
        <InfoSectionCard icon="file-text" title="Notes & Alerts">
          <DetailField label="Notes" value={medicationInfo.notes} />
          <DetailField label="Alerts" value={medicationInfo.alerts} />
        </InfoSectionCard>
      )}

      {/* Info Helper Card */}
      <div
        style={{
          background: 'var(--color-info-bg)',
          border: '1px solid var(--color-info-border)',
          borderRadius: 'var(--radius-md)',
          padding: '17px',
        }}
      >
        <div style={{ display: 'flex', gap: 'var(--sp-3)' }}>
          <div style={{ color: 'var(--color-info)', flexShrink: 0, marginTop: 2 }}>
            <NavIcon name="info" />
          </div>
          <span
            style={{
              fontSize: 'var(--font-size-sm)',
              lineHeight: '19.5px',
              color: '#1e40af',
            }}
          >
            All known allergies and adverse reactions are documented. Click &ldquo;Update
            Medication&rdquo; to add or edit.
          </span>
        </div>
      </div>
    </div>
  );
}
